import dayjs from 'dayjs';
import { 
  X, 
  TrendingUp, 
  Calendar,
  Building2,
  FileSpreadsheet,
  ChartBar,
  Sparkles,
  Activity,
  Package,
  BarChart3,
  ArrowUpRight,
  Layers
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import type {
  ICompanyItemGroupedInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';

import ExcelBtn from './ExcelBtn';
import GroupItemsTable from './Table';

type Props = {
  items: ICompanyItemGroupedInterface[] | null;
  endDate: Date | null;
  selectedCompany: ICompanyList | null;
  startDate: Date | null;
  allTypes: string[];
};

function GroupItemsDialog({
  items,
  endDate,
  selectedCompany,
  startDate,
  allTypes,
}: Props) {
  // Calculate summary statistics
  const summaryStats = items ? {
    totalItems: items.length,
    totalStock: items.reduce((sum, item) => sum + (item.Stock || 0), 0),
    totalStockValue: items.reduce((sum, item) => sum + (item.StockValue || 0), 0),
    totalSalesValue: items.reduce((sum, item) => sum + (item.SalesValue || 0), 0),
  } : null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          size="sm"
          className={cn(
            "relative overflow-hidden transition-all duration-300",
            "bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600",
            "hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700",
            "dark:from-violet-600 dark:via-purple-600 dark:to-indigo-600",
            "dark:hover:from-violet-700 dark:hover:via-purple-700 dark:hover:to-indigo-700",
            "text-white shadow-lg hover:shadow-xl hover:scale-105",
            "border border-white/20",
            "group"
          )}
        >
          <span className="relative z-10 flex items-center gap-2">
            <ChartBar className="h-4 w-4 transition-transform group-hover:rotate-12" />
            <span className="font-medium">اجماليات الاصناف</span>
            <Sparkles className="h-3 w-3 text-yellow-300 opacity-70 group-hover:opacity-100" />
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        </Button>
      </DialogTrigger>
      
      <DialogContent
        className={cn(
          "max-w-[95vw] max-h-[95vh] p-0",
          "bg-gradient-to-br from-background via-background to-muted/10",
          "dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/30",
          "border-border/50 dark:border-slate-700/50 shadow-2xl",
          "flex flex-col overflow-hidden"
        )}
      >
        {/* Enhanced Header */}
        <DialogHeader className={cn(
          "relative p-6 pb-4",
          "bg-gradient-to-r from-violet-50/50 via-purple-50/50 to-indigo-50/50",
          "dark:from-violet-950/30 dark:via-purple-950/30 dark:to-indigo-950/30",
          "border-b border-border/30 dark:border-slate-700/50"
        )}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-grid-black/[0.02] dark:bg-grid-white/[0.02]" />
          </div>
          
          <div className="relative">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                {/* Icon Container */}
                <div className={cn(
                  "relative p-3 rounded-xl",
                  "bg-gradient-to-br from-violet-600 to-indigo-600",
                  "shadow-xl shadow-violet-500/20 dark:shadow-violet-500/10",
                  "group"
                )}>
                  <BarChart3 className="h-6 w-6 text-white" />
                  <div className="absolute -top-1 -right-1">
                    <Sparkles className="h-4 w-4 text-yellow-400 animate-pulse" />
                  </div>
                </div>
                
                {/* Title and Description */}
                <div className="space-y-3">
                  <div>
                    <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-violet-700 to-indigo-700 dark:from-violet-400 dark:to-indigo-400 bg-clip-text text-transparent">
                      تقرير اجماليات حركة الأصناف
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-sm text-muted-foreground">
                      عرض تفصيلي لحركة الأصناف والإجماليات للفترة المحددة
                    </DialogDescription>
                  </div>
                  
                  {/* Info Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={cn(
                      "px-3 py-1.5 gap-1.5",
                      "bg-gradient-to-r from-blue-600 to-blue-500 text-white",
                      "border border-blue-400/30 shadow-sm"
                    )}>
                      <Building2 className="h-3.5 w-3.5" />
                      <span className="font-semibold">{selectedCompany?.Name || 'جميع الشركات'}</span>
                    </Badge>
                    
                    <Badge className={cn(
                      "px-3 py-1.5 gap-1.5",
                      "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white",
                      "border border-emerald-400/30 shadow-sm"
                    )}>
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{dayjs(startDate).format('DD/MM/YYYY')}</span>
                      <ArrowUpRight className="h-3 w-3" />
                      <span>{dayjs(endDate).format('DD/MM/YYYY')}</span>
                    </Badge>
                    
                    {items && (
                      <Badge className={cn(
                        "px-3 py-1.5 gap-1.5",
                        "bg-gradient-to-r from-violet-600 to-violet-500 text-white",
                        "border border-violet-400/30 shadow-sm"
                      )}>
                        <Package className="h-3.5 w-3.5" />
                        <span className="font-bold">{items.length}</span>
                        <span>صنف</span>
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {items && (
                  <ExcelBtn
                    items={items}
                    endDate={endDate}
                    startDate={startDate}
                    selectedCompany={selectedCompany}
                    allTypes={allTypes}
                  />
                )}
                
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "rounded-lg",
                      "hover:bg-red-100 dark:hover:bg-red-950/30",
                      "hover:text-red-600 dark:hover:text-red-400",
                      "transition-all duration-200"
                    )}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </DialogClose>
              </div>
            </div>
            
            {/* Summary Statistics */}
            {summaryStats && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className={cn(
                  "p-3 border-0",
                  "bg-gradient-to-br from-blue-50 to-blue-100/50",
                  "dark:from-blue-950/30 dark:to-blue-900/20"
                )}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">إجمالي الأصناف</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-400">
                        {summaryStats.totalItems.toLocaleString()}
                      </p>
                    </div>
                    <Layers className="h-5 w-5 text-blue-500 opacity-50" />
                  </div>
                </Card>
                
                <Card className={cn(
                  "p-3 border-0",
                  "bg-gradient-to-br from-violet-50 to-violet-100/50",
                  "dark:from-violet-950/30 dark:to-violet-900/20"
                )}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">إجمالي الرصيد</p>
                      <p className="text-lg font-bold text-violet-700 dark:text-violet-400">
                        {Math.round(summaryStats.totalStock).toLocaleString()}
                      </p>
                    </div>
                    <Package className="h-5 w-5 text-violet-500 opacity-50" />
                  </div>
                </Card>
                
                <Card className={cn(
                  "p-3 border-0",
                  "bg-gradient-to-br from-emerald-50 to-emerald-100/50",
                  "dark:from-emerald-950/30 dark:to-emerald-900/20"
                )}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">قيمة الرصيد</p>
                      <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">
                        {Math.round(summaryStats.totalStockValue).toLocaleString()}
                      </p>
                    </div>
                    <TrendingUp className="h-5 w-5 text-emerald-500 opacity-50" />
                  </div>
                </Card>
                
                <Card className={cn(
                  "p-3 border-0",
                  "bg-gradient-to-br from-amber-50 to-amber-100/50",
                  "dark:from-amber-950/30 dark:to-amber-900/20"
                )}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">قيمة المبيعات</p>
                      <p className="text-lg font-bold text-amber-700 dark:text-amber-400">
                        {Math.round(summaryStats.totalSalesValue).toLocaleString()}
                      </p>
                    </div>
                    <Activity className="h-5 w-5 text-amber-500 opacity-50" />
                  </div>
                </Card>
              </div>
            )}
          </div>
        </DialogHeader>
        
        {/* Content Area */}
        <div className="p-4 flex-1 overflow-auto max-h-[calc(95vh-280px)]">
          <Card className={cn(
            "h-full border-0",
            "bg-gradient-to-br from-background/95 to-muted/5",
            "dark:from-slate-900/95 dark:to-slate-800/20",
            "shadow-inner",
            "overflow-hidden"
          )}>
            {items ? (
              <GroupItemsTable items={items} allTypes={allTypes} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center space-y-3">
                  <div className="inline-flex p-4 rounded-full bg-muted/30">
                    <FileSpreadsheet className="h-12 w-12 text-muted-foreground/50" />
                  </div>
                  <p className="text-lg text-muted-foreground">لا توجد بيانات للعرض</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GroupItemsDialog;
